'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import clsx from 'clsx';
import { PropsWithFontFamily, PropsWithLocale } from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import { ProductDetailInfoCTA, useClientData, useUserInfo } from '../../../../client';
import { getConnectedUserInfo, ProductLoyaltyInfoValue } from '../../../../server';
import { PushLoyaltyCTAContent } from './PushLoyaltyCTAContent';
import { PushLoyaltyModal } from './PushLoyaltyModal';
import styles from './loyalty.module.css';
import { VIP_BORDER_CLASSES } from './styles';

const PRODUCT_PUSH_LOYALTY_ID = 'product-push-loyalty';

type ProductDetailPushLoyaltyProps = PropsWithFontFamily &
    PropsWithLocale & {
        currency: string;
        isVipBrand: boolean;
        hasBackCta?: boolean;
        loyaltyInformation: ProductLoyaltyInfoValue;
    };

export const ProductDetailPushLoyalty: Component<ProductDetailPushLoyaltyProps> = ({
    locale,
    currency,
    isVipBrand,
    hasBackCta = false,
    loyaltyInformation,
}) => {
    const t = useTranslations('Discover.Pdp.loyalty');
    const commonT = useTranslations('Modal.pushLoyalty.accessibility');

    const { getClientFlags } = useClientData();
    const isMySephoraActivated = getClientFlags('isMySephoraActivated');

    // Retrieve user information
    const { result: userInfoResult } = useUserInfo();
    const userInfo = useMemo(() => getConnectedUserInfo(userInfoResult), [userInfoResult]);

    const loyaltyPointsLabel = t('label.loyaltyPoints', {
        value: loyaltyInformation.pointsToEarn,
    });

    const customerFirstName = userInfo?.firstName ? `${userInfo.firstName},` : t('label.fidWin');

    return (
        <div
            id={PRODUCT_PUSH_LOYALTY_ID}
            className={clsx(
                'flex flex-col gap-1.5 rounded-0.5',
                isVipBrand ? VIP_BORDER_CLASSES : styles.customGradientFid,
            )}
        >
            <ProductDetailInfoCTA
                menu={
                    <PushLoyaltyModal
                        loyaltyInformation={loyaltyInformation}
                        currency={currency}
                        isVipBrand={isVipBrand}
                        hasBackCta={hasBackCta}
                        isMySephoraActivated={isMySephoraActivated}
                        locale={locale}
                    />
                }
                labelElement={
                    <PushLoyaltyCTAContent
                        loyaltyPointsLabel={loyaltyPointsLabel}
                        userCard={userInfo?.loyalty?.sephoraCard}
                        customerFirstName={customerFirstName}
                        isMySephoraActivated={isMySephoraActivated}
                    />
                }
                className={'text-text-primary p-1'}
                openMenuButtonAccessibilityLabel={commonT('openLoyaltyProgramMenu')}
            />
        </div>
    );
};
